import React, { useEffect, useState } from 'react';
import { COUNTRIES, timeZoneForCountry } from '@pagepilot/domain';
import { Card, Button, Field, Badge, EmptyState, ErrorNote } from '../ui';

export interface PageRow {
  id: string;
  internalLabel: string;
  fbPageId: string;
  fbPageName: string;
  status: string;
  country: string | null;
  primaryLanguage: string | null;
  timeZone: string;
  niche: string | null;
  connection: { connectionStatus: string; lastError: string | null; tokenExpiresAt: string | null } | null;
}

export function PagesScreen(props: { connectionOnly?: boolean; onOpen?: (pageId: string, label: string) => void }) {
  const [pages, setPages] = useState<PageRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [configuring, setConfiguring] = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState<string | null>(null);

  const load = async () => {
    try {
      setPages((await window.pagepilot.pages.list()) as PageRow[]);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">{props.connectionOnly ? 'Facebook Connections' : 'Facebook Pages'}</h1>
        <Button onClick={() => setShowAdd(true)}>Add Page</Button>
      </div>
      {error && <ErrorNote message={error} />}
      {props.connectionOnly && <p className="text-sm text-neutral-400">Add a Page, then select <span className="text-neutral-200">Connect Facebook</span> to store and validate its encrypted Page access token through the official Graph API.</p>}

      {pages.length === 0 && !showAdd ? (
        <EmptyState
          title="No Pages configured"
          hint="Add your first Facebook Page. Each Page gets its own prompts, schedule, branding and publishing pipeline."
          action={<Button onClick={() => setShowAdd(true)}>Add Page</Button>}
        />
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {pages.map((p) => (
            <Card key={p.id}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold">{p.internalLabel}</p>
                  <p className="text-xs text-neutral-500">
                    {p.fbPageName} · {p.fbPageId}
                  </p>
                  <p className="mt-1 text-xs text-neutral-500">
                    {[p.country, p.primaryLanguage, p.timeZone, p.niche].filter(Boolean).join(' · ') || 'Profile not configured yet'}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge status={p.status} />
                  <Badge status={p.connection?.connectionStatus ?? 'DISCONNECTED'} />
                </div>
              </div>
              {p.connection?.lastError && <p className="mt-2 text-xs text-red-400">{p.connection.lastError}</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                {props.onOpen && (
                  <Button onClick={() => props.onOpen!(p.id, p.internalLabel)}>Open workspace</Button>
                )}
                <Button variant="ghost" onClick={() => setConnecting(p.id)}>
                  {p.connection ? 'Reconnect' : 'Connect'} Facebook
                </Button>
                <Button variant="ghost" onClick={() => setConfiguring(configuring === p.id ? null : p.id)}>
                  Configure
                </Button>
                <Button variant="ghost" onClick={() => setDuplicating(duplicating === p.id ? null : p.id)}>
                  Duplicate
                </Button>
                <Button
                  variant="ghost"
                  onClick={async () => {
                    await window.pagepilot.pages.update(p.id, { status: p.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE' });
                    void load();
                  }}
                >
                  {p.status === 'ACTIVE' ? 'Pause' : 'Resume'}
                </Button>
                <Button
                  variant="danger"
                  onClick={async () => {
                    if (confirm(`Archive "${p.internalLabel}"? Scheduled content stops publishing.`)) {
                      await window.pagepilot.pages.archive(p.id);
                      void load();
                    }
                  }}
                >
                  Archive
                </Button>
              </div>
              {connecting === p.id && <ConnectForm pageId={p.id} onDone={() => { setConnecting(null); void load(); }} />}
              {configuring === p.id && <PageConfigForm pageId={p.id} onDone={() => { setConfiguring(null); void load(); }} />}
              {duplicating === p.id && <DuplicatePageForm page={p} onDone={() => { setDuplicating(null); void load(); }} />}
            </Card>
          ))}
        </div>
      )}

      {showAdd && <AddPageForm onDone={() => { setShowAdd(false); void load(); }} />}
    </div>
  );
}

function DuplicatePageForm(props: { page: PageRow; onDone: () => void }) {
  const [label, setLabel] = useState(`${props.page.internalLabel} copy`);
  const [fbPageId, setFbPageId] = useState('');
  const [fbPageName, setFbPageName] = useState(`${props.page.fbPageName} copy`);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <div className="mt-3 rounded-lg border border-neutral-800 bg-neutral-950 p-3">
      <p className="mb-2 text-xs text-neutral-400">
        Copies the Page profile, research settings, schedule, branding settings and prompt assignments. The new Page starts paused; Facebook credentials, media, content and schedules are never copied.
      </p>
      <div className="grid grid-cols-3 gap-3">
        <Field label="New internal label" value={label} onChange={setLabel} />
        <Field label="New Facebook Page ID" value={fbPageId} onChange={setFbPageId} placeholder="Numeric Page ID" />
        <Field label="New Facebook Page name" value={fbPageName} onChange={setFbPageName} />
      </div>
      {err && <div className="mt-2"><ErrorNote message={err} /></div>}
      <div className="mt-3 flex gap-2">
        <Button
          disabled={busy || !label || !fbPageId || !fbPageName}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              await window.pagepilot.pages.duplicate(props.page.id, { internalLabel: label, fbPageId, fbPageName });
              props.onDone();
            } catch (e) {
              setErr(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          Duplicate configuration
        </Button>
        <Button variant="ghost" onClick={props.onDone}>Cancel</Button>
      </div>
    </div>
  );
}

function AddPageForm(props: { onDone: () => void }) {
  const [label, setLabel] = useState('');
  const [fbPageId, setFbPageId] = useState('');
  const [fbPageName, setFbPageName] = useState('');
  const [country, setCountry] = useState('IT');
  const [language, setLanguage] = useState('');
  // Time zone is derived from the country but stays editable for edge cases.
  const [timeZone, setTimeZone] = useState(timeZoneForCountry('IT') ?? 'Europe/Rome');
  const [niche, setNiche] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <Card title="Add Page">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Internal label" value={label} onChange={setLabel} placeholder="e.g. Cuore d'Italia" />
        <Field label="Facebook Page ID" value={fbPageId} onChange={setFbPageId} placeholder="Numeric Page ID" />
        <Field label="Facebook Page name" value={fbPageName} onChange={setFbPageName} />
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-neutral-400">Country</span>
          <select
            value={country}
            onChange={(e) => {
              const code = e.target.value;
              setCountry(code);
              // Selecting a country sets the local time zone used for scheduling.
              const tz = timeZoneForCountry(code);
              if (tz) setTimeZone(tz);
            }}
            className="rounded border border-neutral-700 bg-neutral-900 px-2 py-1"
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </label>
        <Field label="Primary language" value={language} onChange={setLanguage} placeholder="it" />
        <Field label="Time zone (from country)" value={timeZone} onChange={setTimeZone} placeholder="Europe/Rome" />
        <Field label="Niche" value={niche} onChange={setNiche} placeholder="Street interviews" />
      </div>
      {err && <div className="mt-2"><ErrorNote message={err} /></div>}
      <div className="mt-3 flex gap-2">
        <Button
          disabled={busy || !label || !fbPageId || !fbPageName}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              await window.pagepilot.pages.create({
                internalLabel: label,
                fbPageId,
                fbPageName,
                country: country || undefined,
                primaryLanguage: language || undefined,
                timeZone,
                niche: niche || undefined,
              });
              props.onDone();
            } catch (e) {
              setErr(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          Create Page
        </Button>
        <Button variant="ghost" onClick={props.onDone}>Cancel</Button>
      </div>
    </Card>
  );
}

interface ScheduleRow {
  imagePostsPerDay: number;
  preferredTimes: string[];
  approvalMode: string;
  autoApprovalMinScore: number;
  dailyGenerationTime: string;
  missedPostPolicy: string;
}

interface ImageReferenceRow {
  id: string;
  mimeType: string;
  width: number | null;
  height: number | null;
}

export function PageConfigForm(props: { pageId: string; onDone: () => void }) {
  const [feeds, setFeeds] = useState('');
  const [maxAge, setMaxAge] = useState('7');
  const [topicsPerRun, setTopicsPerRun] = useState('6');
  const [postsPerDay, setPostsPerDay] = useState('1');
  const [times, setTimes] = useState('09:00, 20:00');
  const [approvalMode, setApprovalMode] = useState('MANUAL');
  const [minScore, setMinScore] = useState('75');
  const [genTime, setGenTime] = useState('02:00');
  const [missedPolicy, setMissedPolicy] = useState('NEXT_EMPTY_SLOT');
  const [imageReferences, setImageReferences] = useState<ImageReferenceRow[]>([]);
  const [uploadingReference, setUploadingReference] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const research = (await window.pagepilot.pages.getResearch(props.pageId)) as {
          feeds: string[]; maxSourceAgeDays: number; topicsPerRun: number; imageReferenceAssetIds: string[];
        };
        setFeeds(research.feeds.join('\n'));
        setMaxAge(String(research.maxSourceAgeDays));
        setTopicsPerRun(String(research.topicsPerRun));
        setImageReferences((await window.pagepilot.pages.listImageReferences(props.pageId)) as ImageReferenceRow[]);
        const pages = (await window.pagepilot.pages.list()) as { id: string; schedule: ScheduleRow | null }[];
        const s = pages.find((p) => p.id === props.pageId)?.schedule;
        if (s) {
          setPostsPerDay(String(s.imagePostsPerDay));
          setTimes(s.preferredTimes.join(', ') || '09:00, 20:00');
          setApprovalMode(s.approvalMode);
          setMinScore(String(s.autoApprovalMinScore));
          setGenTime(s.dailyGenerationTime);
          setMissedPolicy(s.missedPostPolicy);
        }
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setLoaded(true);
      }
    })();
  }, [props.pageId]);

  if (!loaded) return <p className="mt-3 text-xs text-neutral-500">Loading configuration…</p>;

  const uploadReference = (file: File) => {
    const reader = new FileReader();
    reader.onload = async () => {
      setUploadingReference(true);
      setErr(null);
      try {
        await window.pagepilot.pages.uploadImageReference({ pageId: props.pageId, dataUrl: reader.result as string });
        setImageReferences((await window.pagepilot.pages.listImageReferences(props.pageId)) as ImageReferenceRow[]);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setUploadingReference(false);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-neutral-800 bg-neutral-950 p-3">
      <p className="text-xs font-semibold text-neutral-400">Research sources (one RSS/Atom feed URL per line)</p>
      <textarea
        value={feeds}
        onChange={(e) => setFeeds(e.target.value)}
        spellCheck={false}
        placeholder={'https://www.ansa.it/sito/ansait_rss.xml\nhttps://www.repubblica.it/rss/homepage/rss2.0.xml'}
        className="h-24 w-full resize-none rounded-lg border border-neutral-700 bg-neutral-900 p-2 font-mono text-xs outline-none focus:border-brand-400"
      />
      <div className="rounded-lg border border-neutral-800 p-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-neutral-400">Image-post visual references</p>
            <p className="mt-1 text-[11px] text-neutral-500">Attach up to five examples. New image posts use them for the same visual style, palette, layout and lighting—not as images to copy.</p>
          </div>
          <label className={`shrink-0 cursor-pointer text-xs text-brand-300 hover:underline ${imageReferences.length >= 5 || uploadingReference ? 'pointer-events-none opacity-50' : ''}`}>
            {uploadingReference ? 'Uploading…' : 'Attach image'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              disabled={imageReferences.length >= 5 || uploadingReference}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.currentTarget.value = '';
                if (file) uploadReference(file);
              }}
            />
          </label>
        </div>
        {imageReferences.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {imageReferences.map((reference, index) => (
              <div key={reference.id} className="flex items-center gap-2 rounded border border-neutral-700 bg-neutral-900 px-2 py-1 text-[11px] text-neutral-400">
                <span>Reference {index + 1}{reference.width && reference.height ? ` · ${reference.width}×${reference.height}` : ''}</span>
                <button
                  className="text-red-300 hover:text-red-200"
                  onClick={async () => {
                    try {
                      await window.pagepilot.pages.removeImageReference(props.pageId, reference.id);
                      setImageReferences((await window.pagepilot.pages.listImageReferences(props.pageId)) as ImageReferenceRow[]);
                    } catch (e) {
                      setErr(e instanceof Error ? e.message : String(e));
                    }
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : <p className="mt-2 text-[11px] text-neutral-600">No visual references attached.</p>}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Max source age (days)" value={maxAge} onChange={setMaxAge} />
        <Field label="Topic candidates per run" value={topicsPerRun} onChange={setTopicsPerRun} />
        <Field label="Image posts per day" value={postsPerDay} onChange={setPostsPerDay} />
        <Field label="Preferred times (HH:mm, comma-sep)" value={times} onChange={setTimes} />
        <Field label="Daily generation time" value={genTime} onChange={setGenTime} />
        <Field label="Auto-approval min score" value={minScore} onChange={setMinScore} />
      </div>
      <div className="flex gap-4">
        <label className="text-xs text-neutral-400">
          Approval mode{' '}
          <select value={approvalMode} onChange={(e) => setApprovalMode(e.target.value)} className="ml-1 rounded border border-neutral-700 bg-neutral-900 px-2 py-1">
            <option value="MANUAL">Manual approval</option>
            <option value="AUTO">Automatic (score threshold)</option>
          </select>
        </label>
        <label className="text-xs text-neutral-400">
          Missed-post policy{' '}
          <select value={missedPolicy} onChange={(e) => setMissedPolicy(e.target.value)} className="ml-1 rounded border border-neutral-700 bg-neutral-900 px-2 py-1">
            <option value="PUBLISH_NOW">Publish immediately at startup</option>
            <option value="SKIP">Skip missed content</option>
            <option value="NEXT_EMPTY_SLOT">Move to next empty slot</option>
            <option value="ASK">Ask for review</option>
          </select>
        </label>
      </div>
      {err && <ErrorNote message={err} />}
      {saved && <p className="text-xs text-emerald-400">Saved.</p>}
      <div className="flex gap-2">
        <Button
          onClick={async () => {
            setErr(null);
            setSaved(false);
            try {
              await window.pagepilot.pages.updateResearch(props.pageId, {
                feeds: feeds.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
                maxSourceAgeDays: Number(maxAge) || 7,
                topicsPerRun: Number(topicsPerRun) || 6,
                imageReferenceAssetIds: imageReferences.map((reference) => reference.id),
              });
              await window.pagepilot.pages.updateSchedule(props.pageId, {
                imagePostsPerDay: Number(postsPerDay),
                preferredTimes: times.split(',').map((s) => s.trim()).filter(Boolean),
                approvalMode,
                autoApprovalMinScore: Number(minScore),
                dailyGenerationTime: genTime,
                missedPostPolicy: missedPolicy,
              });
              setSaved(true);
            } catch (e) {
              setErr(e instanceof Error ? e.message : String(e));
            }
          }}
        >
          Save configuration
        </Button>
        <Button variant="ghost" onClick={props.onDone}>Close</Button>
      </div>
    </div>
  );
}

export function ConnectForm(props: { pageId: string; onDone: () => void }) {
  const [token, setToken] = useState('');
  const [apiVersion, setApiVersion] = useState('v23.0');
  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [durable, setDurable] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <div className="mt-3 rounded-lg border border-neutral-800 bg-neutral-950 p-3">
      <label className="mb-2 flex items-center gap-2 text-xs text-neutral-300">
        <input type="checkbox" checked={durable} onChange={(e) => setDurable(e.target.checked)} />
        Keep this connection alive automatically (recommended)
      </label>
      {durable ? (
        <p className="mb-2 text-xs text-neutral-400">
          Paste your <strong>App ID</strong>, <strong>App Secret</strong> (from your Meta app → Settings → Basic) and a{' '}
          <strong>User token</strong> from the Graph API Explorer with <code>pages_manage_posts</code>,{' '}
          <code>pages_read_engagement</code> and <code>pages_show_list</code>. PagePilot exchanges it for a long-lived
          login and derives a <strong>non-expiring Page token</strong>, then refreshes it automatically — so it won't
          expire in an hour. Everything is encrypted with Windows DPAPI and never shown again.
        </p>
      ) : (
        <p className="mb-2 text-xs text-neutral-400">
          Paste a Page access token with <code>pages_manage_posts</code>, <code>pages_read_engagement</code> and{' '}
          <code>pages_show_list</code>. Without an App ID/Secret PagePilot cannot refresh it, so a short-lived token
          will expire. It is encrypted with Windows DPAPI and never shown again.
        </p>
      )}
      {durable && (
        <div className="mb-3 grid grid-cols-2 gap-3">
          <Field label="App ID" value={appId} onChange={setAppId} />
          <Field label="App Secret" type="password" value={appSecret} onChange={setAppSecret} />
        </div>
      )}
      <div className="grid grid-cols-[1fr_120px] gap-3">
        <Field label={durable ? 'User token (or Page token)' : 'Page access token'} type="password" value={token} onChange={setToken} />
        <Field label="Graph API version" value={apiVersion} onChange={setApiVersion} />
      </div>
      {err && <div className="mt-2"><ErrorNote message={err} /></div>}
      <div className="mt-3 flex gap-2">
        <Button
          disabled={busy || token.length < 20 || (durable && (appId.length < 3 || appSecret.length < 10))}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              await window.pagepilot.fb.connect(
                durable
                  ? { pageId: props.pageId, token, apiVersion, appId, appSecret }
                  : { pageId: props.pageId, token, apiVersion },
              );
              props.onDone();
            } catch (e) {
              setErr(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          Save & validate
        </Button>
        <Button variant="ghost" onClick={props.onDone}>Cancel</Button>
      </div>
    </div>
  );
}
